import { useEffect, useRef } from 'react';
import { useSocket } from '@data-phone/react-resource-server-auth';
import { useAppDispatch } from '../redux/hooks';
import { addActiveCall, addAgentOffering, addQueueCall, removeActiveCallBySessionUUID, removeAgentOfferingBySessionUUID,removeAgentOfferingByAgent, removeQueueCallBySessionUUID, AgentOffering, ActiveCall, QueueCall, setActiveCalls, setQueueCalls, addAgentOfferings } from '../redux/slices/callcenter.slice';
import { updateAgentsFromActiveData, updateAgentStatus, updateAgentsDevices, addAgentDevice, removeAgentDevice, updateAgentsPhoneFeatures, resetAgents } from '../redux/slices/agents.slice';
import { ActiveCallCenterResponse } from '../services/activecallcenter.service';
import { getPhoneFeatures, handlePhoneFeature, PhoneFeature } from '../services/phone-features.service';
import { useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
// import { useAccountIdChange } from './useValueChanged';

export const useCallCenterSocket = () => {
  const dispatch = useAppDispatch();
  const { account_id } = useSelector((state: RootState) => state.appConfig);
  const agents = useSelector((state: RootState) => state.agents.items);
  const previousAccountIdRef = useRef<string | null>(null);

  const { socket, changeAccount } = useSocket({
    socketUrl: `${process.env.REACT_APP_LIVE_CALL_WSS}/callCenter`,
    path: "",
  });

  const requestInitData = () => {
    try {
      socket.emit("init-get", (res: any) => {
        const data: ActiveCallCenterResponse | undefined = res?.data || res?.result || res;
        if (data?.agents) {
          applyCallCenterInitialData(dispatch, data);
        }
      });
    } catch (err) {
      console.error("❌ Failed to request init-data:", err);
    }
  };

  const getUserRegistrations = () => {
    try {
      socket.emit("get-user-registrations", (res: any) => {
        if (!res.data || !Array.isArray(res.data)) return;
        
        const agentsMap = new Map<string, any[]>();
        
        res.data.forEach((item: any) => {
          const agentExtension = item.user.split("@")[0];
          const deviceData = {
            networkIp: item.networkIp,
            networkPort: item.networkPort,
            userAgent: item.user_agent,
          };
          
          if (agentsMap.has(agentExtension)) {
            agentsMap.get(agentExtension)!.push(deviceData);
          } else {
            agentsMap.set(agentExtension, [deviceData]);
          }
        });
        
        const agents = Array.from(agentsMap.entries()).map(([agent, devices]) => ({
          agent,
          agentDevices: devices,
        }));
        
        dispatch(updateAgentsDevices({ agents }));
      });
    } catch (error) {
      console.error("Error getting user registrations:", error);
    }
  };

  const handleAgentOnline = (online: boolean, payload: any) => {
    try {
      if (!payload || typeof payload !== "object") {
        throw new Error("Invalid agent-online payload");
      }

      const { user, networkIp, networkPort, userAgent } = payload;

      if (!user) {
        throw new Error("Missing required field 'user' in agent-online/offline payload");
      }

      const agentExtension = user.split("@")[0];

      if (online) {
        dispatch(
          addAgentDevice({
            agent: agentExtension,
            deviceInfo: {
              networkIp: networkIp,
              networkPort: networkPort,
              userAgent: userAgent
            }
          })
        );
      } else {
        dispatch(
          removeAgentDevice({
            agent: agentExtension,
            deviceInfo: {
              networkIp: networkIp,
              networkPort: networkPort,
              userAgent: userAgent
            }
          })
        );
      }
    } catch (error) {
      console.error("Error handling agent-online:", error);
    }
  };

  const getPhoneFeaturesForAgents = () => {
    getPhoneFeatures(socket, (phoneFeatures) => {
      dispatch(updateAgentsPhoneFeatures(phoneFeatures));
    });
  };

  const handleAgentPhoneFeature = (payload: PhoneFeature) => {
    handlePhoneFeature(payload, (phoneFeature) => {
      dispatch(updateAgentsPhoneFeatures([phoneFeature]));
    });
  };

  useEffect(() => {
    if (!account_id) return;
    if (!socket) return;

    changeAccount(account_id);
  }, [account_id, socket]);

  useEffect(() => {
    if (!agents.length || !account_id) return;

    if (previousAccountIdRef.current !== account_id) {
      getUserRegistrations();
      getPhoneFeaturesForAgents();
      requestInitData();
      previousAccountIdRef.current = account_id;
    }
  }, [agents, account_id])

  useEffect(() => {
    if (!socket) return;

    socket.on("callcenter-update", (data) => {
      if (data.event === "member-queue-start" || data.event === "member-queue-resume") {
        dispatch(addQueueCall(data));
      }

      if (data.event == "agent-state-change") {
        if (data.state == "Waiting") {
          dispatch(removeAgentOfferingByAgent(data.name));
        }
        dispatch(updateAgentStatus(data))
      }

      if (data.event === "bridge-agent-start") {
        dispatch(removeQueueCallBySessionUUID(data.memberSessionUUID));
        dispatch(addActiveCall(data));
        dispatch(removeAgentOfferingBySessionUUID(data.sessionUUID));
      }

      if (data.event === "member-queue-end") {
        dispatch(removeAgentOfferingBySessionUUID(data.sessionUUID));
        dispatch(removeQueueCallBySessionUUID(data.sessionUUID));
        dispatch(removeActiveCallBySessionUUID(data.sessionUUID));
      }

      if (data.event === "bridge-agent-end") {
        dispatch(removeActiveCallBySessionUUID(data.memberSessionUUID));
        dispatch(removeAgentOfferingBySessionUUID(data.sessionUUID));
      }

      if (data.event === "agent-offering") {
        dispatch(addAgentOffering(data));
      }

      if (data.event === "stale-member") {
        dispatch(removeQueueCallBySessionUUID(data?.uuid));
        dispatch(removeAgentOfferingBySessionUUID(data?.uuid));
      }
    });

    socket.on("auth-failure", (data) => {
    });

    socket.on("disconnect", (reason) => {
    });

    socket.on("connect_error", (err) => {
      console.error("❌ Call Center Socket connect error:", err);
    });

    socket.on("user-online", (data: any) => {
     try {
      
        handleAgentOnline(true, data);
     } catch (error) {
      console.error("Error handling agent-online:", error);
     }
    });

    socket.on("user-offline", (data: any) => {
      try {
        handleAgentOnline(false, data);
      } catch (error) {
        console.error("Error handling agent-offline:", error);
      }
    });

    socket.on("phone-feature", handleAgentPhoneFeature);

    return () => {
      socket.off("callcenter-update");
      socket.off("auth-failure");
      socket.off("disconnect");
      socket.off("connect_error");
      socket.off("user-online");
      socket.off("user-offline");
      socket.off("phone-feature");
      socket.disconnect();
    };
  }, [socket, dispatch]);
};

const applyCallCenterInitialData = (
  dispatch: AppDispatch,
  activeCallCenterData: ActiveCallCenterResponse
) => {
  if (!activeCallCenterData) return;

  dispatch(updateAgentsFromActiveData(activeCallCenterData));

  const agentOfferings: AgentOffering[] = [];
  const activeCalls: ActiveCall[] = [];
  const queueCalls: QueueCall[] = [];

  activeCallCenterData.queues?.forEach((queue) => {
    if (!queue.members?.length) return;

    queue.members.forEach((member) => {
      if (member.state === "Trying") {
        queueCalls.push({
          memberCIDName: member.callerIDName || '',
          memberCIDNumber: member.callerIDNumber || '',
          joinedEpoch: member.joinedEpoch || 0,
          queueName: queue.name,
          sessionUUID: member.sessionUUID || '',
          queue: queue.name,
          agentName: '',
        });
      } else if (member.state === "Answered") {
        activeCalls.push({
          memberCIDName: member.callerIDName || '',
          memberCIDNumber: member.callerIDNumber || '',
          joinedEpoch: member.joinedEpoch || 0,
          calledTime: member.joinedEpoch || 0,
          queueName: queue.name,
          agentName: '',
          memberSessionUUID: member.sessionUUID || '',
          name: member.agent || '',
        });
      }
    });

    // Process agent offerings for trying members with offering tiers
    if (queue.tiers?.length) {
      const tryingMembers = queue.members.filter(member => member.state === "Trying");
      if (tryingMembers.length > 0) {
        const offeringTiers = queue.tiers.filter(tier => tier.state === "Offering");
        if (offeringTiers.length > 0) {
          tryingMembers.forEach((member) => {
            offeringTiers.forEach((tier) => {
              agentOfferings.push({
                uuid: member?.uuid || '',
                sessionUUID: member?.sessionUUID || '',
                queue: queue?.name || '',
                callerIDName: member?.callerIDName || '',
                callerIDNumber: member?.callerIDNumber || '',
                event: "agent-offering",
                domain: "",
                agent: `${tier?.agent || ''}`,
              });
            });
          });
        }
      }
    }
  });

  // Dispatch all updates
  dispatch(setActiveCalls(activeCalls));
  dispatch(setQueueCalls(queueCalls));
  dispatch(addAgentOfferings(agentOfferings));
};

export default useCallCenterSocket;
